import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

const API_BASE = '/api';

/** Upload token validation response */
export interface UploadInfoResponse {
  valid: boolean;
  error?: string;
  clientName?: string;
  assessmentRef?: string;
  documentTypes?: string[];
  remainingUploads?: number;
}

/** Document request info */
export interface DocumentRequestInfo {
  id: string;
  documentType: string;
  otherDescription?: string | null;
  received: boolean;
  dateRequested: string;
}

/** Upload result response */
export interface UploadResultResponse {
  success: boolean;
  uploadedFiles: Array<{
    id: string;
    documentRequestId: string;
    originalFilename: string;
    mimeType: string;
    sizeBytes: number;
  }>;
  errors: string[];
}

/** Query keys for upload feature */
export const uploadKeys = {
  all: ['upload'] as const,
  token: (token: string) => [...uploadKeys.all, 'token', token] as const,
  documentRequests: (token: string) => [...uploadKeys.all, 'document-requests', token] as const,
};

/**
 * Validate an upload token (public endpoint, no auth required)
 */
export async function validateUploadToken(token: string): Promise<UploadInfoResponse> {
  const response = await fetch(`${API_BASE}/upload/${token}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to validate token' }));
    throw new Error(error.message);
  }

  return response.json();
}

/**
 * Get document requests for a token (public endpoint)
 */
export async function getDocumentRequests(token: string): Promise<DocumentRequestInfo[]> {
  const response = await fetch(`${API_BASE}/upload/${token}/document-requests`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Failed to get document requests' }));
    throw new Error(error.message);
  }

  return response.json();
}

/**
 * Upload files (public endpoint, no auth required)
 */
export async function uploadFiles(
  token: string,
  documentRequestId: string,
  files: File[],
): Promise<UploadResultResponse> {
  const formData = new FormData();
  formData.append('documentRequestId', documentRequestId);
  for (const file of files) {
    formData.append('files', file);
  }

  const response = await fetch(`${API_BASE}/upload/${token}`, {
    method: 'POST',
    body: formData,
    // Don't set Content-Type - browser will set it with boundary for multipart
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Upload failed' }));
    throw new Error(error.message);
  }

  return response.json();
}

/** Hook to validate upload token */
export function useValidateUploadToken(token: string) {
  return useQuery({
    queryKey: uploadKeys.token(token),
    queryFn: () => validateUploadToken(token),
    enabled: !!token,
    retry: false, // Don't retry on failure - token is either valid or not
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/** Hook to get document requests */
export function useDocumentRequests(token: string, enabled = true) {
  return useQuery({
    queryKey: uploadKeys.documentRequests(token),
    queryFn: () => getDocumentRequests(token),
    enabled: !!token && enabled,
    retry: false,
  });
}

/** Hook to upload files */
export function useUploadFiles(onSuccess?: () => void) {
  return useMutation({
    mutationFn: ({
      token,
      documentRequestId,
      files,
    }: {
      token: string;
      documentRequestId: string;
      files: File[];
    }) => uploadFiles(token, documentRequestId, files),
    onSuccess: (data) => {
      if (data.success) {
        toast.success(`${data.uploadedFiles.length} file(s) uploaded successfully`);
        onSuccess?.();
      } else if (data.errors.length > 0) {
        toast.error(data.errors[0]);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Upload failed');
    },
  });
}
